import { makeAutoObservable, runInAction } from "mobx";
import agent from "../api/agent";

export interface EmailLog {
  id: string;
  recipients: string[];
  subject: string;
  body: string;
  sentAt: string;
  status: string;
}

export default class EmailLogStore {
  logs: EmailLog[] = [];
  loading = false;

  constructor() {
    makeAutoObservable(this);
  }

  fetchLogs = async () => {
    this.loading = true;
    try {
      const logs = await agent.Emails.getLogs();
      runInAction(() => {
        this.logs = logs;
      });
    } catch (error) {
      console.error("Failed to fetch email logs:", error);
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  downloadLogPdf = async (id: string) => {
    try {
      const blob = await agent.Emails.downloadLogPdf(id);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `email-log-${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
    } catch (error) {
      console.error("Failed to download PDF:", error);
    }
  };

  sendEmail = async (payload: { memberIds: string[]; templateId?: string; body?: string }) => {
    try {
      await agent.Emails.send(payload);
      this.fetchLogs();
    } catch (error) {
      console.error("Failed to send email:", error);
    }
  };

  generateLetterPdf = async (memberId: string, templateId?: string, body?: string) => {
    const blob = await agent.Emails.generateLetterPdf({ memberId, templateId, body });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `letter-${memberId}.pdf`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  generateBatchLettersPdf = async (memberIds: string[], templateId?: string, body?: string) => {
    try {
      const blob = await agent.Emails.generateBatchLettersPdf({ memberIds, templateId, body });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `letters-batch.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to generate batch letters PDF:", error);
    }
  };

  getLettersForMember = async (memberId: string) => {
    return await agent.Emails.getLettersForMember(memberId);
  };

  getBatchInfos = async () => {
    return await agent.Emails.getBatchInfos();
  };

  downloadBatchPdf = async (batchId: string) => {
    const blob = await agent.Emails.downloadBatchPdf(batchId);
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `letters-batch-${batchId}.pdf`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  downloadLetterPdf = async (letterId: string) => {
    const blob = await agent.Emails.downloadLetterPdf(letterId);
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `letter-${letterId}.pdf`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  deleteBatch = async (batchId: string) => {
    await agent.Emails.deleteBatch(batchId);
  };
}
